/** Maps a thrown error to a stable code; consumers own the localized copy, and the core touches no DOM. */
import type { RecordingMode } from "../model/recording-mode";

/** Stable error codes for capture/permission failures. */
export enum RecordingErrorCode {
  /** Required mic/camera not found (`NotFoundError` / `DevicesNotFoundError`). */
  DeviceNotFound = "device-not-found",
  /** User denied media permission (`NotAllowedError` / `PermissionDeniedError`). */
  PermissionDenied = "permission-denied",
  /** Requested constraints unsupported by the device (`OverconstrainedError`). */
  OverConstrained = "over-constrained",
  /** Device busy or hardware error (`NotReadableError`). */
  DeviceInUse = "device-in-use",
  /** Insecure context — HTTPS required (`SecurityError`). */
  InsecureContext = "insecure-context",
  /** Capability unsupported in this browser (`NotSupportedError`). */
  NotSupported = "not-supported",
  /** User cancelled, such as dismissing the screen picker; not fatal, so consumers should suppress the error banner. */
  UserCancelled = "user-cancelled",
  /** Capture aborted unexpectedly (`AbortError` outside screen mode). */
  Aborted = "aborted",
  /** Anything else — unknown DOMException or non-Error value. */
  Unknown = "unknown",
}

/** Context that influences classification. */
export interface RecordingErrorContext {
  /** The capture mode in effect — screen-mode `AbortError` is `UserCancelled`. */
  mode?: RecordingMode;
}

/** Classify a thrown error; non-DOMException is `Unknown`, and `AbortError` is `UserCancelled` only in screen mode. */
export function mapRecordingError(
  err: unknown,
  ctx: RecordingErrorContext = {},
): RecordingErrorCode {
  if (!(err instanceof DOMException)) {
    return RecordingErrorCode.Unknown;
  }

  switch (err.name) {
    case "NotFoundError":
    case "DevicesNotFoundError":
      return RecordingErrorCode.DeviceNotFound;

    case "NotAllowedError":
    case "PermissionDeniedError":
      return RecordingErrorCode.PermissionDenied;

    case "OverconstrainedError":
      return RecordingErrorCode.OverConstrained;

    case "NotReadableError":
      return RecordingErrorCode.DeviceInUse;

    case "SecurityError":
      return RecordingErrorCode.InsecureContext;

    case "NotSupportedError":
      return RecordingErrorCode.NotSupported;

    case "AbortError":
      // Screen-mode AbortError = user dismissed the screen-picker dialog.
      return ctx.mode === "screen"
        ? RecordingErrorCode.UserCancelled
        : RecordingErrorCode.Aborted;

    default:
      return RecordingErrorCode.Unknown;
  }
}
